import { CircularProgress, Tooltip } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { DatasetAndSync } from "@dashboard-v3/api";
import StatusIcon from "components/StatusIcon";
import { SyncStatusLabel } from "../Styled";

type Props = Pick<DatasetAndSync, "hadSync" | "syncEnabled" | "lastRun">;

/**
 * Como le fue al ultimo sync, en el header del item (MXH-5792).
 *
 * Antes esto era un "Sync enabled" verde: decia que el dataset tenia sync, no
 * si andaba. Un sync que venia fallando hace semanas se veia igual que uno
 * sano, y la unica forma de enterarse era entrar al dataset y mirar la lista de
 * runs.
 *
 * El estado sale del ultimo run y no de la config, que es lo que cambia la
 * respuesta: `syncEnabled` solo se usa cuando todavia no corrio nada.
 */
export default function SyncStatus({ hadSync, syncEnabled, lastRun }: Props) {
  const { t } = useTranslation("datasets");

  if (!hadSync) return null;

  switch (lastRun?.status) {
    // Los dos son terminales y quedan igual de rotos para el usuario: el
    // dataset no se actualizo y nadie va a reintentar solo.
    case "ERROR":
    case "WAITING_RETRY_CANCELED":
      return (
        <Label
          testId="datasets_sync-status-failed"
          at={lastRun.endsAt ?? lastRun.startedAt}
          text={t("listItem.syncFailed", { defaultValue: "Sync failed" })}
        >
          <StatusIcon state="disable" />
        </Label>
      );

    // Fallo, pero se esta reintentando solo: amarillo y no rojo, porque todavia
    // puede terminar bien y no hay nada que hacer al respecto.
    case "WAITING_RETRY":
      return (
        <Label
          testId="datasets_sync-status-retrying"
          at={lastRun.startedAt}
          text={t("listItem.syncRetrying", { defaultValue: "Retrying sync" })}
        >
          <StatusIcon state="warning" />
        </Label>
      );

    case "PROCESSING":
      return (
        <Label
          testId="datasets_sync-status-processing"
          at={lastRun.startedAt}
          text={t("listItem.syncProcessing", { defaultValue: "Syncing…" })}
        >
          <CircularProgress size={12} style={{ marginRight: "6px" }} />
        </Label>
      );

    case "DONE":
      return (
        <Label
          testId="datasets_sync-status-done"
          at={lastRun.endsAt ?? lastRun.startedAt}
          text={t("listItem.syncDone", { defaultValue: "Synced" })}
        >
          <StatusIcon state="enable" />
        </Label>
      );

    default:
      // Sync configurado que todavia no corrio. Es el unico caso donde el
      // estado depende de la config: sin runs no hay otra cosa que decir.
      if (!syncEnabled) return null;

      return (
        <Label
          testId="datasets_sync-status-enabled"
          text={t("listItem.syncEnabled")}
        >
          <StatusIcon state="enable" />
        </Label>
      );
  }
}

/**
 * El texto corto va en el header y la fecha en el tooltip: la fila ya tiene el
 * nombre, el `updatedAt` y tres botones, y "Sync failed on 12/3/2026, 4:03 PM"
 * la parte en dos en pantallas angostas.
 */
function Label({
  children,
  text,
  at,
  testId,
}: {
  children: React.ReactNode;
  text: string;
  at?: number | null;
  testId: string;
}) {
  const label = (
    <SyncStatusLabel data-testid={testId}>
      {children}
      {text}
    </SyncStatusLabel>
  );

  if (!at) return label;

  // Mismo formato que la lista de runs del detalle, para que la fecha del
  // header y la del run se lean como lo que son: la misma corrida.
  return <Tooltip title={new Date(at).toLocaleString()}>{label}</Tooltip>;
}
